import { takeEvery, call, put, all } from 'redux-saga/effects';
import firestoreRedux from '@dreamworld/firestore-redux';
import { retry } from '@lifeomic/attempt';
import { requestApi, isNetworkError } from '../../../request-api';
import * as actions from './actions.js';
import { store } from '../../../store.js';
import * as aiChatActions from '../../ai-chat/actions.js';

/**
 * Map of attachmentId → AbortController for in-progress uploads.
 * Used by cancelUploadSaga to abort HTTP requests, and by the pagehide
 * handler to abort all in-flight uploads when the user confirms a reload.
 */
const abortControllers = new Map();

/**
 * Prompt the user before reload/close while uploads are in progress.
 * NOTE: Modern Chromium/Firefox/Safari ignore this string and show their own
 * generic message. Setting it has no visible effect outside legacy browsers.
 */
function _handleBeforeUnload(event) {
  event.preventDefault();
  event.returnValue = 'Uploading files will be removed.';
  return 'Uploading files will be removed.';
}

/**
 * After the user confirms the reload prompt, abort every in-flight upload and
 * synchronously strip the corresponding chips from every persisted draft
 * before the page tears down.
 *
 * We deliberately skip `cancelUpload` here: its CANCELED localWrite would
 * sync back into the chip's `details` attribute, which Squire picks up as a
 * `value-changed` event and re-persists the chip into the draft after our
 * cleanup. Aborting the controller directly stops the HTTP request without
 * mutating Redux, so a single synchronous `removeDraftChipsByIds` dispatch
 * is all persistEnhancer needs to flush to localStorage.
 */
function _cleanupUploadsOnPageExit() {
  if (abortControllers.size === 0) return;

  const ids = Array.from(abortControllers.keys());
  for (const controller of abortControllers.values()) {
    try { controller.abort(); } catch { /* ignore */ }
  }
  abortControllers.clear();

  store.dispatch(aiChatActions.removeDraftChipsByIds(ids));
}

/**
 * Handles `load` action. Subscribes to Firestore collection with retry for permission errors.
 * @param {Object} action - { type, chatId }
 */
function* loadSaga(action) {
  const { chatId } = action;
  if (!chatId) return;

  try {
    const requesterId = `ai-chat-attachments-${chatId}`;
    yield call(() => retry(() => {
      const query = firestoreRedux.query('ai-chat-attachments', {
        id: requesterId,
        where: [['chatId', '==', chatId]],
        orderBy: [['createdAt', 'asc']],
        requesterId,
      });
      return query.result;
    }, { maxAttempts: 3, factor: 2, delay: 2000, maxDelay: 5000 }));
  } catch (error) {
    console.error('Failed to load AI chat attachments:', error);
  }
}

/**
 * Handles `disconnect` action. Unsubscribes from Firestore collection.
 * @param {Object} action - { type, chatId }
 */
function* disconnectSaga(action) {
  const { chatId } = action;
  if (!chatId) return;
  firestoreRedux.cancelQueryByRequester(`ai-chat-attachments-${chatId}`);
}

/**
 * Handles `upload` action.
 * Receives prepared entries (IDs already generated, chips already inserted by ai-chat-input).
 * Creates optimistic localWrite docs and uploads files in parallel.
 */
function* uploadSaga(action) {
  const { entries, chatId } = action;
  // Ensure Firestore subscription is active before uploading.
  // For new chats, loadChatDataSaga hasn't run yet, so without this the real
  // Firestore doc (status: SUCCESS) would never arrive in Redux after the
  // localWrite is deleted on success.
  yield put(actions.load(chatId));
  yield all(entries.map((entry) => call(uploadSingleFile, chatId, entry)));
}

/**
 * Uploads a single file.
 * @param {String} chatId Chat ID
 * @param {{ id: String, file: File }} fileEntry
 */
function* uploadSingleFile(chatId, { id, file }) {
  const collectionPath = `ai-chats/${chatId}/ai-chat-attachments`;
  const abortController = new AbortController();
  abortControllers.set(id, abortController);
  if (abortControllers.size === 1) {
    window.addEventListener('beforeunload', _handleBeforeUnload);
    window.addEventListener('pagehide', _cleanupUploadsOnPageExit);
  }

  try {
    // Optimistic localWrite
    const doc = {
      id,
      chatId,
      status: 'IN_PROGRESS',
      uploading: true,
      contentType: file.type || 'file/other',
      type: 'FILE',
      title: file.name,
      createdAt: Date.now(),
    };
    firestoreRedux.save(collectionPath, doc, { localWrite: true });

    // POST upload
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('chatId', chatId);
    formdata.append('file', file);
    formdata.append('title', file.name);
    formdata.append('fileContentLength', file.size);

    const options = {
      method: 'POST',
      body: formdata,
      signal: abortController.signal,
      excludeErrors: [400, 409],
    };
    yield call(requestApi, '/attachment2/ai-chat-attachments/upload-file', options);

    firestoreRedux.delete(collectionPath, id, { localWrite: true });
    yield put(actions._uploadSuccess(chatId, id));
  } catch (error) {
    if (isNetworkError(error) || error.code === error.ABORT_ERR) {
      // Cleanup for abort is handled by cancelUploadSaga (saves CANCELED status).
      return;
    }

    // Set error on local doc so chip shows error state
    firestoreRedux.save(collectionPath, { id, uploading: false, status: 'FAILED', error: error.code || 'Upload failed' }, { localWrite: true });

    yield put(actions._uploadFailed(chatId, id, error.code || 'Upload failed'));
    console.error('ai-chat-attachment upload failed:', error);
  } finally {
    abortControllers.delete(id);
    if (abortControllers.size === 0) {
      window.removeEventListener('beforeunload', _handleBeforeUnload);
      window.removeEventListener('pagehide', _cleanupUploadsOnPageExit);
    }
  }
}

/**
 * Handles `cancelUpload` action. Aborts in-progress HTTP request and marks the
 * local doc as CANCELED (rather than deleting it) so that `waitForUploads` in the
 * ai-chat saga can detect the removal and clear any pending auto-submit state.
 * @param {Object} action - { type, chatId, attachmentId }
 */
function* cancelUploadSaga(action) {
  const { chatId, attachmentId } = action;
  const collectionPath = `ai-chats/${chatId}/ai-chat-attachments`;

  const controller = abortControllers.get(attachmentId);
  if (controller) {
    controller.abort();
    // Save CANCELED so waitForUploads sees counts.canceled > 0 and returns 'FAILED',
    // which clears auto-submit. The catch block in uploadSingleFile skips cleanup
    // for abort errors, leaving this CANCELED doc in place.
    firestoreRedux.save(collectionPath, { id: attachmentId, status: 'CANCELED' }, { localWrite: true });
  } else {
    firestoreRedux.delete(collectionPath, attachmentId, { localWrite: true });
  }
  yield put(actions._cancelUploadSuccess(chatId, attachmentId));
}

/**
 * Handles `deleteAttachment` action. Calls DELETE API to remove uploaded attachment.
 * @param {Object} action - { type, chatId, attachmentId }
 */
function* deleteAttachmentSaga() {
  // No-op: server automatically removes orphan attachments after every N days.
}

/**
 * Returns the AbortController for a given attachment ID.
 * Used by cancelUploadSaga.
 * @param {String} attachmentId
 * @returns {AbortController|undefined}
 */
export const getAbortController = (attachmentId) => abortControllers.get(attachmentId);

export default function* saga() {
  yield takeEvery(actions.LOAD, loadSaga);
  yield takeEvery(actions.DISCONNECT, disconnectSaga);
  yield takeEvery(actions.UPLOAD, uploadSaga);
  yield takeEvery(actions.CANCEL_UPLOAD, cancelUploadSaga);
  yield takeEvery(actions.DELETE_ATTACHMENT, deleteAttachmentSaga);
}
